import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { CustomResponseEntity, PaginationParams } from '../types/common';
import {
  CityDto,
  CorregimientoDto,
  CountryDto,
  CreateCityDto,
  CreateCorregimientoDto,
  CreateCountryDto,
  CreateRegionDto,
  CreateVeredaDto,
  RegionDto,
  UpdateCityDto,
  UpdateCorregimientoDto,
  UpdateCountryDto,
  UpdateRegionDto,
  UpdateVeredaDto,
  VeredaDto,
} from '../types/dtos';
import {
  cityService,
  corregimientoService,
  countryService,
  regionService,
  veredaService,
} from '../services/location.service';
import { api } from '../client/axiosClient';
import { ENDPOINTS } from '../endpoints';

export interface LocationDto {
  id: string;
  name: string;
}

export interface LocationItem {
  id: string;
  name: string;
}

export interface LocationResponse {
  data: LocationItem[];
}

// Query keys
export const locationKeys = {
  all: ['location'] as const,
  countries: () => [...locationKeys.all, 'countries'] as const,
  country: (id: string) => [...locationKeys.countries(), id] as const,
  states: (countryId: string) => [...locationKeys.all, 'regions', countryId] as const,
  regions: () => [...locationKeys.all, 'regions'] as const,
  region: (id: string) => [...locationKeys.regions(), id] as const,
  cities: (stateId: string) => [...locationKeys.all, 'cities', stateId] as const,
  city: (id: string) => [...locationKeys.cities(''), id] as const,
  corregimientos: () => [...locationKeys.all, 'corregimientos'] as const,
  corregimiento: (id: string) => [...locationKeys.corregimientos(), id] as const,
  veredas: () => [...locationKeys.all, 'veredas'] as const,
  veredasByCity: (cityId: string) => [...locationKeys.all, 'veredas', 'city', cityId] as const,
  vereda: (id: string) => [...locationKeys.veredas(), id] as const,
};

// Hooks for location filters
export const useCountries = () => {
  return useQuery<CustomResponseEntity<LocationDto[]>>({
    queryKey: locationKeys.countries(),
    queryFn: () => api.get<CustomResponseEntity<LocationDto[]>>(ENDPOINTS.countries.base())
  });
};

export const useStates = (countryId: string | undefined) => {
  return useQuery<CustomResponseEntity<LocationDto[]>>({
    queryKey: locationKeys.states(countryId || ''),
    queryFn: () => api.get<CustomResponseEntity<LocationDto[]>>(
      `${ENDPOINTS.regions.base()}?countryId=${countryId}`
    ),
    enabled: !!countryId
  });
};

export const useCities = (stateId: string | undefined) => {
  return useQuery<CustomResponseEntity<LocationDto[]>>({
    queryKey: locationKeys.cities(stateId || ''),
    queryFn: () => api.get<CustomResponseEntity<LocationDto[]>>(
      ENDPOINTS.cities.byRegion(stateId!)
    ),
    enabled: !!stateId && stateId !== 'undefined'
  });
};

// Countries
export const useCountry = (id: string, options?: UseQueryOptions<CustomResponseEntity<CountryDto>>) => {
  return useQuery({
    queryKey: locationKeys.country(id),
    queryFn: () => countryService.findById(id),
    ...options,
  });
};

export const useCreateCountry = () => {
  return useMutation({
    mutationFn: (data: CreateCountryDto) => countryService.create(data),
  });
};

export const useUpdateCountry = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCountryDto }) =>
      countryService.update(id, data),
  });
};

export const useDeleteCountry = () => {
  return useMutation({
    mutationFn: (id: string) => countryService.delete(id),
  });
};

// Regions
export const useRegions = (params?: PaginationParams, options?: UseQueryOptions<CustomResponseEntity<RegionDto[]>>) => {
  return useQuery({
    queryKey: locationKeys.regions(),
    queryFn: () => regionService.findAll(params),
    ...options,
  });
};

export const useRegion = (id: string, options?: UseQueryOptions<CustomResponseEntity<RegionDto>>) => {
  return useQuery({
    queryKey: locationKeys.region(id),
    queryFn: () => regionService.findById(id),
    ...options,
  });
};

export const useCreateRegion = () => {
  return useMutation({
    mutationFn: (data: CreateRegionDto) => regionService.create(data),
  });
};

export const useUpdateRegion = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRegionDto }) =>
      regionService.update(id, data),
  });
};

export const useDeleteRegion = () => {
  return useMutation({
    mutationFn: (id: string) => regionService.delete(id),
  });
};

// Cities
export const useCity = (id: string | undefined, options?: UseQueryOptions<CustomResponseEntity<CityDto>>) => {
  return useQuery({
    queryKey: locationKeys.city(id || ''),
    queryFn: () => cityService.findById(id!),
    enabled: !!id && id !== 'undefined',
    ...options,
  });
};

export const useCreateCity = () => {
  return useMutation({
    mutationFn: (data: CreateCityDto) => cityService.create(data),
  });
};

export const useUpdateCity = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCityDto }) =>
      cityService.update(id, data),
  });
};

export const useDeleteCity = () => {
  return useMutation({
    mutationFn: (id: string) => cityService.delete(id),
  });
};

// Corregimientos
export const useCorregimientos = (
  params?: PaginationParams,
  options?: UseQueryOptions<CustomResponseEntity<CorregimientoDto[]>>
) => {
  return useQuery({
    queryKey: locationKeys.corregimientos(),
    queryFn: () => corregimientoService.findAll(params),
    ...options,
  });
};

export const useCorregimiento = (id: string, options?: UseQueryOptions<CustomResponseEntity<CorregimientoDto>>) => {
  return useQuery({
    queryKey: locationKeys.corregimiento(id),
    queryFn: () => corregimientoService.findById(id),
    ...options,
  });
};

export const useCreateCorregimiento = () => {
  return useMutation({
    mutationFn: (data: CreateCorregimientoDto) => corregimientoService.create(data),
  });
};

export const useUpdateCorregimiento = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCorregimientoDto }) =>
      corregimientoService.update(id, data),
  });
};

export const useDeleteCorregimiento = () => {
  return useMutation({
    mutationFn: (id: string) => corregimientoService.delete(id),
  });
};

// Veredas
export const useVeredas = (params?: PaginationParams, options?: UseQueryOptions<CustomResponseEntity<VeredaDto[]>>) => {
  return useQuery({
    queryKey: locationKeys.veredas(),
    queryFn: () => veredaService.findAll(params),
    ...options,
  });
};

export const useVeredasByCity = (cityId: string | undefined) => {
  return useQuery<CustomResponseEntity<VeredaDto[]>>({
    queryKey: locationKeys.veredasByCity(cityId || ''),
    queryFn: () => api.get<CustomResponseEntity<VeredaDto[]>>(
      ENDPOINTS.veredas.byCity(cityId!)
    ),
    enabled: !!cityId
  });
};

export const useVereda = (id: string, options?: UseQueryOptions<CustomResponseEntity<VeredaDto>>) => {
  return useQuery({
    queryKey: locationKeys.vereda(id),
    queryFn: () => veredaService.findById(id),
    ...options,
  });
};

export const useCreateVereda = () => {
  return useMutation({
    mutationFn: (data: CreateVeredaDto) => veredaService.create(data),
  });
};

export const useUpdateVereda = () => {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVeredaDto }) =>
      veredaService.update(id, data),
  });
};

export const useDeleteVereda = () => {
  return useMutation({
    mutationFn: (id: string) => veredaService.delete(id),
  });
}; 